import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { ClipboardList, ExternalLink, PackageCheck, Plus, Printer, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { Label } from '@/components/ui/Label';
import { DataTable, arrIncludesSome, type FacetedFilter } from '@/components/ui/DataTable';
import { MetricCard } from '@/components/financials/MetricCard';
import { PurchaseOrderDialog } from '@/components/procurement/PurchaseOrderDialog';
import { ReceiveDialog } from '@/components/procurement/ReceiveDialog';
import {
  PO_CHARGE_KIND_LABELS,
  PO_CLOSED_STATUSES,
  PO_RECEIVABLE_STATUSES,
  PO_STATUSES,
  PO_STATUS_LABELS,
  PO_STATUS_VARIANTS,
  PURCHASE_METHODS,
  PURCHASE_METHOD_LABELS,
  TRACKING_STATUS_LABELS,
  purchaseOrderTrackingUrl,
  purchaseCardLabel,
  poLineLandedCents,
  poLineName,
  purchaseMethodLabel,
  usePurchaseCards,
  usePurchaseOrders,
  useRefreshPurchaseOrderTracking,
  type PoShipment,
  type PurchaseOrder,
} from '@/features/procurement/hooks';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { formatCurrency, formatDateOnly, formatUnitCurrency, todayIsoDate } from '@/lib/formatters';
import { printDocument } from '@/lib/printDocument';

const STATUS_OPTIONS = PO_STATUSES.map((s) => ({ value: s, label: PO_STATUS_LABELS[s] }));

/** Filter / sort key for the Method column: the card id for card POs, otherwise the method. */
function methodKey(po: PurchaseOrder): string {
  if (po.purchase_method === 'card')
    return po.purchase_card_id ? `card:${po.purchase_card_id}` : 'card';
  return po.purchase_method ?? '';
}

function receivedUnits(po: PurchaseOrder) {
  return po.po_lines.reduce(
    (acc, l) => ({
      ordered: acc.ordered + l.quantity,
      received: acc.received + l.received_quantity,
    }),
    { ordered: 0, received: 0 },
  );
}

function isOverdue(po: PurchaseOrder, today: string) {
  const expected = effectiveExpectedAt(po);
  return !!expected && expected < today && !PO_CLOSED_STATUSES.includes(po.status);
}

function effectiveExpectedAt(po: PurchaseOrder) {
  return po.tracking_estimated_delivery ?? po.expected_at;
}

function shipmentLabel(s: PoShipment): string {
  return [s.carrier, s.tracking_number].filter(Boolean).join(' · ');
}

function printPurchaseOrder(po: PurchaseOrder, cards: ReturnType<typeof usePurchaseCards>['data']) {
  const method = purchaseMethodLabel(po.purchase_method, po.purchase_card_id, cards);
  const hasTariffs = po.po_lines.some((line) => (line.tariff_cents ?? 0) > 0);
  // Shipping and other charges are shared across the lines (migration 157);
  // the printed PO shows each line's share and what it lands at.
  const hasChargeShares = po.po_lines.some(
    (line) => (line.shipping_alloc_cents ?? 0) + (line.other_charges_alloc_cents ?? 0) > 0,
  );
  const landedGoodsCents = po.po_lines.reduce((acc, line) => acc + poLineLandedCents(line), 0);
  const shipments = po.po_shipments;
  const opened = printDocument({
    title: 'Purchase order',
    number: po.po_number,
    status: PO_STATUS_LABELS[po.status],
    subtitle: po.vendors?.name ?? undefined,
    sections: [
      {
        title: 'Order details',
        fields: [
          { label: 'Vendor', value: po.vendors?.name },
          {
            label: 'Ship to',
            value: po.ship_to ? `${po.ship_to.name} (${po.ship_to.code})` : null,
          },
          { label: 'Purchase method', value: method },
          { label: 'Payment terms', value: po.payment_terms },
          { label: 'Order date', value: formatDateOnly(po.ordered_at) },
          { label: 'Expected delivery', value: formatDateOnly(effectiveExpectedAt(po)) },
          { label: 'Ordered on', value: po.order_platform },
          { label: 'Vendor order #', value: po.order_reference },
          {
            label: 'Tracking',
            value:
              shipments.length > 1
                ? `${shipments.length} shipments (listed below)`
                : shipments.length === 1
                  ? shipmentLabel(shipments[0])
                  : [po.carrier, po.tracking_number].filter(Boolean).join(' · '),
          },
        ],
      },
      ...(shipments.length > 1
        ? [
            {
              title: 'Shipments',
              table: {
                columns: [
                  { label: 'Carrier' },
                  { label: 'Tracking #' },
                  { label: 'Contents' },
                  { label: 'Status' },
                  { label: 'Est. delivery' },
                ],
                rows: shipments.map((s) => [
                  s.carrier,
                  s.tracking_number,
                  s.description,
                  s.tracking_status ? TRACKING_STATUS_LABELS[s.tracking_status] : null,
                  formatDateOnly(s.tracking_estimated_delivery),
                ]),
              },
            },
          ]
        : []),
      {
        title: 'Line items',
        table: {
          columns: [
            { label: 'SKU' },
            { label: 'Supplier part #' },
            { label: 'Item' },
            { label: 'Qty', align: 'right' },
            { label: 'Received', align: 'right' },
            { label: 'Unit cost', align: 'right' },
            ...(hasTariffs ? [{ label: 'Tariff', align: 'right' as const }] : []),
            { label: 'Total', align: 'right' },
            ...(hasChargeShares
              ? [
                  { label: 'Shipping & charges share', align: 'right' as const },
                  { label: 'Landed', align: 'right' as const },
                ]
              : []),
          ],
          rows: po.po_lines.map((line) => [
            line.items?.sku ?? (line.item_id ? null : 'Custom'),
            line.supplier_part_number,
            poLineName(line),
            `${line.quantity.toLocaleString()} ${line.uom}`,
            `${line.received_quantity.toLocaleString()} ${line.uom}`,
            `${formatUnitCurrency(line.unit_price_cents)}/${line.uom}`,
            ...(hasTariffs ? [formatCurrency(line.tariff_cents ?? 0)] : []),
            formatCurrency(line.line_total_cents + (line.tariff_cents ?? 0)),
            ...(hasChargeShares
              ? [
                  formatCurrency(
                    (line.shipping_alloc_cents ?? 0) + (line.other_charges_alloc_cents ?? 0),
                  ),
                  `${formatCurrency(poLineLandedCents(line))} (${formatUnitCurrency(
                    poLineLandedCents(line) / Math.max(1, line.quantity),
                  )}/${line.uom})`,
                ]
              : []),
          ]),
        },
      },
      ...(po.po_shipping_charges.length
        ? [
            {
              title: 'Shipping & other charges',
              table: {
                columns: [
                  { label: 'Type' },
                  { label: 'Description' },
                  { label: 'Reference' },
                  { label: 'Date' },
                  { label: 'Amount', align: 'right' as const },
                ],
                rows: po.po_shipping_charges.map((charge) => [
                  PO_CHARGE_KIND_LABELS[charge.kind ?? 'shipping'],
                  charge.description,
                  charge.reference,
                  formatDateOnly(charge.charged_at),
                  formatCurrency(charge.amount_cents),
                ]),
              },
            },
          ]
        : []),
      {
        title: 'Totals',
        fields: [
          { label: 'Subtotal', value: formatCurrency(po.subtotal_cents) },
          ...(hasTariffs
            ? [{ label: 'Tariffs', value: formatCurrency(po.tariff_cents ?? 0) }]
            : []),
          { label: 'Tax', value: formatCurrency(po.tax_cents) },
          { label: 'Shipping', value: formatCurrency(po.shipping_cents) },
          ...(po.other_charges_cents
            ? [{ label: 'Other charges', value: formatCurrency(po.other_charges_cents) }]
            : []),
          { label: 'Processing fees', value: formatCurrency(po.processing_fee_cents) },
          { label: 'Total', value: formatCurrency(po.total_cents) },
          ...(hasTariffs || hasChargeShares
            ? [
                {
                  label: 'Landed cost of goods (excl. tax & fees)',
                  value: formatCurrency(landedGoodsCents),
                },
              ]
            : []),
        ],
      },
      ...(po.notes ? [{ title: 'Notes', text: po.notes }] : []),
    ],
  });
  if (!opened) toast.error('Allow pop-ups to print or save this purchase order as a PDF.');
}

function isTrackingDelayed(po: PurchaseOrder) {
  return (
    !!po.expected_at &&
    !!po.tracking_estimated_delivery &&
    po.tracking_estimated_delivery > po.expected_at
  );
}

/**
 * Purchase orders — every PO in the org, open ones by default. Admins
 * create / edit POs from here and jump straight into a receiving form;
 * a PO only reaches Delivered once receiving covers every line.
 */
export function PurchaseOrdersPage() {
  const { role } = useAuth();
  const { data, isLoading } = usePurchaseOrders();
  const { data: purchaseCards } = usePurchaseCards();
  const refreshTracking = useRefreshPurchaseOrderTracking();
  const [showClosed, setShowClosed] = useState(false);
  const [editing, setEditing] = useState<PurchaseOrder | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [receivePoId, setReceivePoId] = useState<string | null>(null);
  const [receiveOpen, setReceiveOpen] = useState(false);

  const canManage = canWrite(role, 'procurement');
  const today = todayIsoDate();
  const all = useMemo<PurchaseOrder[]>(() => data ?? [], [data]);

  // `?po=<id>` opens that PO straight away (the procurement calendar links
  // here after drafting one). Consumed once so a refresh shows the list.
  const [params, setParams] = useSearchParams();
  const requestedPoId = params.get('po');
  useEffect(() => {
    if (!requestedPoId || !data) return;
    const po = data.find((p) => p.id === requestedPoId);
    if (!po) toast.error('That purchase order isn’t in the list any more.');
    else if (canManage) {
      setEditing(po);
      setFormOpen(true);
    }
    const next = new URLSearchParams(params);
    next.delete('po');
    setParams(next, { replace: true });
  }, [requestedPoId, data, canManage, params, setParams]);

  // `?new=1[&item=<id>]` opens a blank PO, optionally seeded with that item
  // (the item detail page links here). Consumed once like `?po=`.
  const [initialItemId, setInitialItemId] = useState<string | null>(null);
  const requestedNew = params.get('new');
  useEffect(() => {
    if (!requestedNew) return;
    if (canManage) {
      setEditing(null);
      setInitialItemId(params.get('item'));
      setFormOpen(true);
    }
    const next = new URLSearchParams(params);
    next.delete('new');
    next.delete('item');
    setParams(next, { replace: true });
  }, [requestedNew, canManage, params, setParams]);
  const rows = useMemo(
    () => (showClosed ? all : all.filter((po) => !PO_CLOSED_STATUSES.includes(po.status))),
    [all, showClosed],
  );

  const stats = useMemo(() => {
    const open = all.filter((po) => !PO_CLOSED_STATUSES.includes(po.status));
    return {
      open: open.length,
      openValue: open.reduce((acc, po) => acc + (po.total_cents ?? 0), 0),
      inTransit: open.filter((po) => po.status === 'shipped').length,
      overdue: open.filter((po) => isOverdue(po, today)).length,
      delayed: open.filter(isTrackingDelayed).length,
    };
  }, [all, today]);

  const openNew = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (po: PurchaseOrder) => {
    setEditing(po);
    setFormOpen(true);
  };
  const openReceive = (poId: string | null) => {
    setReceivePoId(poId);
    setReceiveOpen(true);
  };

  const vendorOptions = useMemo(() => {
    const names = new Set(all.map((po) => po.vendors?.name).filter((n): n is string => !!n));
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((n) => ({ value: n, label: n }));
  }, [all]);

  const methodOptions = useMemo(() => {
    const used = new Set(all.map(methodKey).filter(Boolean));
    const cardOptions = (purchaseCards ?? [])
      .filter((c) => used.has(`card:${c.id}`))
      .map((c) => ({ value: `card:${c.id}`, label: purchaseCardLabel(c) }));
    const otherOptions = PURCHASE_METHODS.filter((m) => m !== 'card' && used.has(m)).map((m) => ({
      value: m,
      label: PURCHASE_METHOD_LABELS[m],
    }));
    return [...cardOptions, ...otherOptions];
  }, [all, purchaseCards]);

  const columns = useMemo<ColumnDef<PurchaseOrder>[]>(
    () => [
      {
        id: 'po_number',
        accessorKey: 'po_number',
        header: 'PO #',
        size: 110,
        minSize: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap font-mono text-xs font-medium text-foreground">
            {row.original.po_number}
          </span>
        ),
      },
      {
        id: 'vendor',
        accessorFn: (r) => r.vendors?.name ?? '',
        header: 'Vendor',
        size: 180,
        minSize: 120,
        filterFn: arrIncludesSome,
        cell: ({ row }) => <span className="font-medium">{row.original.vendors?.name ?? '—'}</span>,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        size: 150,
        minSize: 110,
        filterFn: arrIncludesSome,
        cell: ({ row }) => (
          <Badge variant={PO_STATUS_VARIANTS[row.original.status]}>
            {PO_STATUS_LABELS[row.original.status] ?? row.original.status}
          </Badge>
        ),
      },
      {
        id: 'items',
        accessorFn: (r) =>
          r.po_lines
            .map(
              (l) =>
                `${l.items?.sku ?? ''} ${l.supplier_part_number ?? ''} ${poLineName(l)} ${l.description ?? ''}`,
            )
            .join(' '),
        header: 'Items',
        size: 240,
        minSize: 140,
        enableSorting: false,
        cell: ({ row }) => {
          const lines = row.original.po_lines;
          if (lines.length === 0) return <span className="text-muted-foreground">—</span>;
          const first = lines[0];
          return (
            <span className="text-sm">
              {poLineName(first)}
              {!first.item_id && <span className="ml-1 text-xs text-muted-foreground">custom</span>}
              {lines.length > 1 && (
                <span className="ml-1 text-xs text-muted-foreground">+{lines.length - 1} more</span>
              )}
            </span>
          );
        },
      },
      {
        id: 'method',
        accessorFn: methodKey,
        header: 'Method',
        size: 170,
        minSize: 90,
        filterFn: arrIncludesSome,
        cell: ({ row }) => {
          const m = row.original.purchase_method;
          if (!m) return <span className="text-muted-foreground">—</span>;
          return (
            <span className="whitespace-nowrap">
              {purchaseMethodLabel(m, row.original.purchase_card_id, purchaseCards)}
              {m === 'terms' && row.original.payment_terms && (
                <span className="ml-1 text-xs text-muted-foreground">
                  ({row.original.payment_terms})
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: 'ordered_at',
        accessorFn: (r) => r.ordered_at ?? '',
        header: 'Ordered',
        size: 110,
        minSize: 90,
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {formatDateOnly(row.original.ordered_at)}
          </span>
        ),
      },
      {
        id: 'expected_at',
        accessorFn: (r) => r.expected_at ?? '',
        header: 'Expected',
        size: 130,
        minSize: 100,
        cell: ({ row }) => {
          const po = row.original;
          const overdue = isOverdue(po, today);
          const delayed = isTrackingDelayed(po);
          return (
            <span className="flex items-center gap-1.5 whitespace-nowrap">
              <span className={overdue ? 'text-fresh-salmon-dark' : 'text-muted-foreground'}>
                {formatDateOnly(effectiveExpectedAt(po))}
              </span>
              {delayed ? (
                <Badge variant="warning">Delayed</Badge>
              ) : overdue ? (
                <Badge variant="warning">Late</Badge>
              ) : null}
            </span>
          );
        },
      },
      {
        id: 'tracking',
        accessorFn: (r) =>
          r.po_shipments
            .map((s) => `${s.carrier ?? ''} ${s.tracking_number}`.trim())
            .join(' ')
            .trim(),
        header: 'Tracking',
        size: 170,
        minSize: 110,
        enableSorting: false,
        cell: ({ row }) => {
          const po = row.original;
          const shipments = po.po_shipments;
          if (!shipments.length) return <span className="text-muted-foreground">—</span>;
          // Two parcels fit the cell; the rest are summarised with a hover list.
          const shown = shipments.slice(0, 2);
          const hidden = shipments.slice(2);
          return (
            <div className="flex items-center gap-1.5">
              <div className="min-w-0 text-xs">
                {shown.map((s) => {
                  const href = purchaseOrderTrackingUrl(s);
                  const status = s.tracking_status
                    ? TRACKING_STATUS_LABELS[s.tracking_status]
                    : null;
                  return (
                    <div
                      key={s.id}
                      className="truncate"
                      title={[shipmentLabel(s), s.description, status].filter(Boolean).join(' · ')}
                    >
                      {s.carrier && <span className="mr-1 text-muted-foreground">{s.carrier}</span>}
                      {href ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-foreground hover:underline"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {s.tracking_number}
                          <ExternalLink className="ml-1 inline h-3 w-3" />
                        </a>
                      ) : (
                        <span className="font-mono">{s.tracking_number}</span>
                      )}
                      {shipments.length > 1 && status && (
                        <span className="ml-1 text-muted-foreground">· {status}</span>
                      )}
                    </div>
                  );
                })}
                {hidden.length > 0 && (
                  <div
                    className="text-muted-foreground"
                    title={hidden
                      .map((s) =>
                        [
                          shipmentLabel(s),
                          s.description,
                          s.tracking_status ? TRACKING_STATUS_LABELS[s.tracking_status] : null,
                        ]
                          .filter(Boolean)
                          .join(' · '),
                      )
                      .join('\n')}
                  >
                    +{hidden.length} more
                  </div>
                )}
                {shipments.length === 1 && po.tracking_status && (
                  <div className="mt-0.5 truncate text-muted-foreground">
                    {TRACKING_STATUS_LABELS[po.tracking_status]}
                  </div>
                )}
              </div>
              {canManage && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 shrink-0"
                  title="Refresh carrier tracking"
                  disabled={refreshTracking.isPending}
                  onClick={(event) => {
                    event.stopPropagation();
                    refreshTracking.mutate(po.id, {
                      onSuccess: () => toast.success(`Tracking refreshed for ${po.po_number}`),
                      onError: (error) =>
                        toast.error(
                          error instanceof Error ? error.message : 'Tracking refresh failed.',
                        ),
                    });
                  }}
                >
                  <RefreshCw
                    className={`h-3.5 w-3.5 ${refreshTracking.isPending ? 'animate-spin' : ''}`}
                  />
                </Button>
              )}
            </div>
          );
        },
      },
      {
        id: 'received',
        accessorFn: (r) => {
          const u = receivedUnits(r);
          return u.ordered ? u.received / u.ordered : 0;
        },
        header: () => <span className="block text-right">Received</span>,
        size: 110,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => {
          const u = receivedUnits(row.original);
          return (
            <span className="block text-right tabular-nums text-muted-foreground">
              {u.received.toLocaleString()} / {u.ordered.toLocaleString()}
            </span>
          );
        },
      },
      {
        id: 'total',
        accessorKey: 'total_cents',
        header: () => <span className="block text-right">Total</span>,
        size: 110,
        minSize: 90,
        sortingFn: 'basic',
        cell: ({ row }) => (
          <span className="block text-right tabular-nums">
            {formatCurrency(row.original.total_cents)}
          </span>
        ),
      },
      {
        id: '__print',
        header: '',
        size: 48,
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            title="Print or save as PDF"
            aria-label={`Print ${row.original.po_number}`}
            onClick={(event) => {
              event.stopPropagation();
              printPurchaseOrder(row.original, purchaseCards);
            }}
          >
            <Printer className="h-4 w-4" />
          </Button>
        ),
      },
      ...(canManage
        ? [
            {
              id: '__receive',
              header: '',
              size: 100,
              enableSorting: false,
              cell: ({ row }) =>
                PO_RECEIVABLE_STATUSES.includes(row.original.status) ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      openReceive(row.original.id);
                    }}
                  >
                    <PackageCheck className="h-3.5 w-3.5" />
                    Receive
                  </Button>
                ) : null,
            } satisfies ColumnDef<PurchaseOrder>,
          ]
        : []),
    ],
    [canManage, today, purchaseCards, refreshTracking],
  );

  const facetedFilters: FacetedFilter[] = useMemo(
    () => [
      { columnId: 'status', title: 'Status', options: STATUS_OPTIONS },
      { columnId: 'vendor', title: 'Vendor', options: vendorOptions },
      { columnId: 'method', title: 'Method', options: methodOptions },
    ],
    [vendorOptions, methodOptions],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Purchase orders"
        actions={
          canManage ? (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => openReceive(null)}>
                <PackageCheck className="h-4 w-4" />
                Receive
              </Button>
              <Button onClick={openNew}>
                <Plus className="h-4 w-4" />
                New PO
              </Button>
            </div>
          ) : undefined
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <MetricCard label="Open POs" value={stats.open.toLocaleString()} loading={isLoading} />
        <MetricCard
          label="Open PO value"
          value={formatCurrency(stats.openValue, { whole: true })}
          loading={isLoading}
        />
        <MetricCard
          label="On the way"
          value={stats.inTransit.toLocaleString()}
          loading={isLoading}
        />
        <MetricCard
          label="Carrier delays"
          value={stats.delayed.toLocaleString()}
          loading={isLoading}
        />
        <MetricCard
          label="Past expected date"
          value={stats.overdue.toLocaleString()}
          loading={isLoading}
        />
      </div>

      <Card>
        <CardContent className="pt-6">
          <DataTable<PurchaseOrder, unknown>
            data={rows}
            columns={columns}
            isLoading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={(po) => (canManage ? openEdit(po) : printPurchaseOrder(po, purchaseCards))}
            rowClassName={(r) =>
              PO_CLOSED_STATUSES.includes(r.status)
                ? 'opacity-70'
                : isTrackingDelayed(r)
                  ? 'bg-fresh-salmon/5'
                  : undefined
            }
            searchPlaceholder="Search by PO #, vendor, SKU, supplier part #, item, or tracking…"
            facetedFilters={facetedFilters}
            initialPageSize={25}
            pageSizes={[25, 50, 100]}
            tableId="procurement-purchase-orders"
            enableColumnResizing
            toolbarSlot={
              <div className="flex items-center gap-2">
                <Switch id="po-show-closed" checked={showClosed} onCheckedChange={setShowClosed} />
                <Label
                  htmlFor="po-show-closed"
                  className="cursor-pointer text-xs text-muted-foreground"
                >
                  Show delivered &amp; cancelled
                </Label>
              </div>
            }
            mobileCard={{
              title: (po) => po.vendors?.name ?? '—',
              subtitle: (po) => <span className="font-mono text-[11px]">{po.po_number}</span>,
              badges: (po) => [
                <Badge key="status" variant={PO_STATUS_VARIANTS[po.status]}>
                  {PO_STATUS_LABELS[po.status]}
                </Badge>,
                isTrackingDelayed(po) ? (
                  <Badge key="delayed" variant="warning">
                    Delayed
                  </Badge>
                ) : isOverdue(po, today) ? (
                  <Badge key="late" variant="warning">
                    Late
                  </Badge>
                ) : null,
              ],
              amount: (po) => ({ value: formatCurrency(po.total_cents), tone: 'neutral' }),
              meta: (po) => {
                const u = receivedUnits(po);
                return [
                  { label: 'Expected', value: formatDateOnly(effectiveExpectedAt(po)) },
                  { label: 'Received', value: `${u.received} / ${u.ordered}` },
                ];
              },
              dimmed: (po) => PO_CLOSED_STATUSES.includes(po.status),
            }}
            emptyState={
              <EmptyState
                icon={ClipboardList}
                title={
                  all.length > 0 && !showClosed
                    ? 'No open purchase orders'
                    : 'No purchase orders yet'
                }
                action={
                  canManage ? (
                    <Button onClick={openNew}>
                      <Plus className="h-4 w-4" />
                      New PO
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      {canManage && (
        <>
          <PurchaseOrderDialog
            open={formOpen}
            onOpenChange={(open) => {
              setFormOpen(open);
              if (!open) setInitialItemId(null);
            }}
            purchaseOrder={editing}
            initialItemId={editing ? null : initialItemId}
            onPrint={(po) => printPurchaseOrder(po, purchaseCards)}
          />
          <ReceiveDialog
            open={receiveOpen}
            onOpenChange={setReceiveOpen}
            purchaseOrderId={receivePoId}
          />
        </>
      )}
    </div>
  );
}
